import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, ShieldCheck, Smile } from "lucide-react";
import { OUTPUT_QUICK_ACTIONS } from "@/config/outputQuickActions";

interface RegenerateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegenerate: (feedback?: string) => void;
  isGenerating: boolean;
}

export function RegenerateModal({
  isOpen,
  onClose,
  onRegenerate,
  isGenerating,
}: RegenerateModalProps) {
  const [feedback, setFeedback] = useState("");

  const handleSubmit = () => {
    onRegenerate(feedback.trim() || undefined);
    setFeedback("");
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>What do you want the AI to improve?</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Quick actions */}
          <div className="grid grid-cols-1 gap-2">
            {OUTPUT_QUICK_ACTIONS.map((action) => (
              <Button
                key={action.id}
                variant="outline"
                onClick={() => {
                  onRegenerate(action.promptTemplate);
                  setFeedback("");
                  onClose();
                }}
                disabled={isGenerating}
                className="w-full justify-start items-start gap-3 p-4 h-auto text-left"
              >
                {action.id === "humanize" ? (
                  <Smile className="h-4 w-4" />
                ) : (
                  <ShieldCheck className="h-4 w-4" />
                )}
                <div className="text-left">
                  <div className="text-sm font-medium leading-5">
                    {action.label}
                  </div>
                  {action.description && (
                    <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {action.description}
                    </div>
                  )}
                </div>
              </Button>
            ))}
          </div>

          <div className="h-px bg-gray-200" />

          <div className="text-xs text-muted-foreground">Something else?</div>
          <Textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Tell the AI how to improve the content (e.g., 'Make it more professional', 'Add more examples', 'Use more emojis')..."
            className="min-h-[100px] text-sm"
          />
        </div>

        <DialogFooter>
          <Button
            onClick={handleSubmit}
            disabled={isGenerating}
            className="w-full flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-[rgb(158,172,211)] border border-[rgb(158,172,211)] hover:border-[rgb(140,156,195)] hover:text-[rgb(140,156,195)]"
          >
            <Sparkles className="h-4 w-4" />
            {isGenerating ? "Improving..." : "Improve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
